import React, { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import type { IProps, IThemeContext, TResolvedTheme, TTheme } from './interfaces'

const STORAGE_KEY = 'wijmobiel-theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

function readStoredTheme(): TTheme {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  return 'system'
}

export const ThemeContext = createContext<IThemeContext | null>(null)

export const ThemeProvider = ({ children }: IProps): React.JSX.Element => {
  const [theme, setTheme] = useState<TTheme>(readStoredTheme)
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => window.matchMedia(DARK_QUERY).matches)

  const resolvedTheme = useMemo((): TResolvedTheme => {
    if (theme === 'system') return systemPrefersDark ? 'dark' : 'light'
    return theme
  }, [theme, systemPrefersDark])

  const updateTheme = useCallback((next: TTheme): void => {
    setTheme(next)
  }, [])

  const toggleTheme = useCallback((): void => {
    setTheme(resolvedTheme === 'light' ? 'dark' : 'light')
  }, [resolvedTheme])

  const contextValue = useMemo(
    (): IThemeContext => ({ theme, resolvedTheme, setTheme: updateTheme, toggleTheme }),
    [theme, resolvedTheme, updateTheme, toggleTheme],
  )

  // useLayoutEffect so flushSync inside startViewTransition captures the correct "after" snapshot
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme)
    localStorage.setItem(STORAGE_KEY, theme)
  }, [theme, resolvedTheme])

  useEffect(() => {
    const mql = window.matchMedia(DARK_QUERY)

    const handleChange = (event: MediaQueryListEvent): void => {
      setSystemPrefersDark(event.matches)
    }

    mql.addEventListener('change', handleChange)

    return (): void => {
      mql.removeEventListener('change', handleChange)
    }
  }, [])

  return <ThemeContext value={contextValue}>{children}</ThemeContext>
}
